import React, { useRef, useState } from 'react';
import {
  Paper,
  Typography,
  Button,
  Box,
  Alert,
  useTheme
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';

const ResumeUpload = ({ onUpload }) => {
  const theme = useTheme();
  const fileInputRef = useRef(null);
  const [file, setFile] = useState(null);
  const [error, setError] = useState(null);

  const handleFileChange = async (e) => {
    const selectedFile = e.target.files[0];
    if (!selectedFile) return;

    if (!selectedFile.type.includes('pdf') &&
      !selectedFile.type.includes('document') &&
      !selectedFile.type.includes('msword')) {
      setError('Please upload a PDF or Word document');
      return;
    }

    setFile(selectedFile);
    setError(null);

    const reader = new FileReader();
    // In ResumeUpload.js handleFileChange function
    reader.onload = async (event) => {
      try {
        console.log("File loaded:", selectedFile.name, selectedFile.type);
        console.log("File size:", event.target.result.byteLength, "bytes");

        // Convert the binary data to base64
        const base64String = btoa(
          new Uint8Array(event.target.result)
            .reduce((data, byte) => data + String.fromCharCode(byte & 0xff), '')
        );

        console.log("Base64 conversion completed, length:", base64String.length);

        const uploadData = {
          name: selectedFile.name,
          type: selectedFile.type,
          content: base64String
        };

        console.log("Sending upload data:", uploadData.name, uploadData.type);
        onUpload(uploadData);
      } catch (err) {
        console.error('Error processing file:', err);
        setError('Failed to process resume: ' + err.message);
      }
    };
    reader.readAsArrayBuffer(selectedFile);
  };

  return (
    <Paper
      elevation={0}
      sx={{
        p: 3,
        borderRadius: 3,
        border: `1px solid ${theme.palette.divider}`,
        background: theme.palette.blue['900'],
        transition: 'transform 0.2s, box-shadow 0.2s',
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: theme.shadows[4]
        }
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <DescriptionOutlinedIcon sx={{ color: theme.palette.white['100'] }} />
        <Typography variant="h6" sx={{ fontWeight: 600, color: theme.palette.white['100'] }}>
          Resume Upload
        </Typography>
      </Box>

      <Typography variant="body2" color={theme.palette.white['100']} sx={{ mb: 3 }}>
        Upload your resume to match with the job requirements
      </Typography>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, bgcolor: theme.palette.white['100'] }}>
        <input
          type="file"
          ref={fileInputRef}
          onChange={handleFileChange}
          accept=".pdf,.doc,.docx"
          style={{ display: 'none' }}
        />

        <Button
          onClick={() => fileInputRef.current?.click()}
          variant="outlined"
          sx={{
            height: '160px',
            border: '2px dashed',
            borderRadius: 3,
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
            transition: 'all 0.2s',
            '&:hover': {
              backgroundColor: theme.palette.action.hover
            }
          }}
        >
          {file ? (
            <>
              <CheckCircleIcon color="success" sx={{ fontSize: 40 }} />
              <Box>
                <Typography variant="subtitle1" color="primary" sx={{ fontWeight: 600 }}>
                  Resume Uploaded
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {file.name}
                </Typography>
              </Box>
            </>
          ) : (
            <>
              <CloudUploadIcon sx={{ fontSize: 40 }} />
              <Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  Drop your resume here
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Supports PDF & Word documents
                </Typography>
              </Box>
            </>
          )}
        </Button>

        {error && (
          <Alert
            severity="error"
            sx={{
              borderRadius: 2,
              '& .MuiAlert-message': {
                width: '100%'
              }
            }}
          >
            {error}
          </Alert>
        )}
      </Box>
    </Paper>
  );
};

export default ResumeUpload;